import type { FactionId } from '../../shared/types';

/**
 * Ultimate「戰力槽」charge rates.
 *
 * BUG FIX (boss「空格按不了」2026-05-30): the old timePctPerSec=1.5 meant a
 * pure-time fill took ~67s — longer than most fights — so the kid pressed
 * space/⚡ and nothing happened (looked broken). Re-tuned so a normal fight
 * fills the gauge in ~15-25s: time 1.5→5 (full in ~20s standing still),
 * killNormal 5→8, killElite 15→20. Kills + time together comfortably hit a
 * cast every fight. Keyboard/touch wiring was always fine — only the rate was.
 */
export const CHARGE = {
  timePctPerSec: 5,
  killNormalPct: 8,
  killElitePct: 20,
  damagePctPerHp: 0.5,
} as const;

export function factionMultiplier(factionXP: number): number {
  return 1 + factionXP / 1000;
}

export class ChargingState {
  private chargePct = 0; // 0..100

  onTick(dtSec: number, multiplier: number): void {
    this.chargePct = Math.min(100, this.chargePct + CHARGE.timePctPerSec * dtSec * multiplier);
  }

  onEnemyKilled(multiplier: number, elite: boolean = false): void {
    const base = elite ? CHARGE.killElitePct : CHARGE.killNormalPct;
    this.chargePct = Math.min(100, this.chargePct + base * multiplier);
  }

  onPlayerDamaged(hpLost: number, multiplier: number): void {
    this.chargePct = Math.min(100, this.chargePct + CHARGE.damagePctPerHp * hpLost * multiplier);
  }

  canActivate(): boolean {
    return this.chargePct >= 100;
  }

  drain(): void {
    this.chargePct = 0;
  }

  progressFraction(): number {
    return this.chargePct / 100;
  }
}

/**
 * Interface implemented by all loadout skills. BattleScene drives lifecycle:
 * - charging.onTick / onEnemyKilled / onPlayerDamaged during update
 * - activate(out) called on player key press if charging.canActivate()
 *
 * level: 1-based skill level set by BattleScene at loadout construction time.
 * Default is 1 (base stats). Skills read this in activate() to scale their output.
 */
export interface SkillInstance {
  readonly id: string;
  readonly faction: FactionId;
  charging: ChargingState;
  level: number;
  activate(out: ActivationOutput): void;
}

/**
 * Effects a skill can declare during activate(). BattleScene implements this
 * and runs the actual Phaser-side side-effects.
 *
 * Required methods: implemented by all 5 lv1 skills + BattleScene.
 * Optional methods (marked with ?): added for lv2/3 skill stubs; BattleScene
 * will implement them in a follow-up batch (W3 faction agents). Calls via
 * `out.method?.()` are safe no-ops until then.
 */
export interface ActivationOutput {
  fireBullet(angleRad: number, damage: number, sizeMultiplier: number, kind: 'normal' | 'meteor'): void;
  applyShield(hitsAbsorbed: number): void;
  freezeAllEnemies(durationMs: number, dpsTick: number): void;
  setBulletDamageMult(mult: number, durationMs: number): void;
  /** ShadowDrone: open a scan window during which ALL bullet hits get +bonusDmg,
   *  enemies are tinted pale cyan, and other loadout skills receive a one-time
   *  +allyBoostPct% charge boost. */
  startScan(durationMs: number, bonusDmg: number, allyBoostPct: number): void;
  buffAutoFire(intervalMs: number, durationMs: number): void;
  /** EternalSunshine: accrue +perSec freeXP each second for durationMs,
   *  folded into the end-of-battle reward. */
  accrueFreeXPDuringBuff(perSec: number, durationMs: number): void;
  playSound(kind: 'shoot' | 'hit' | 'explode', gain: number): void;
  shake(durationMs: number, intensity: number): void;

  // ── Optional methods for lv2/3 skill stubs ─────────────────────────────
  /** celestial-2: fire `count` homing bullets each dealing `damage`. */
  spawnHomingBullets?(count: number, damage: number): void;
  /** celestial-3: set enemy speed multiplier for `durationMs`. */
  setEnemySpeedMult?(mult: number, durationMs: number): void;
  /** runic-2: stop all enemies for `durationMs` (boss duration scaled by bossFactor, default 0.5). */
  stopAllEnemies?(durationMs: number, bossFactor?: number): void;
  /** runic-3: cancel the boss's next special attack. */
  cancelBossNextAttack?(): void;
  /** techno-2: stun all enemies (no movement, no attack) for `durationMs`. */
  stunAllEnemies?(durationMs: number): void;
  /** techno-3: spawn a piercing bullet that ignores boss shield when `ignoreBossShield` is true. */
  spawnPierceBullet?(damage: number, ignoreBossShield: boolean): void;
  /** elemental-2: spawn a fire pillar DoT field lasting `durationMs`, dealing `dmgPerTick` every `intervalMs`. */
  spawnFirePillar?(durationMs: number, dmgPerTick: number, intervalMs: number): void;
  /** elemental-3: multiply all player damage by `mult` for `durationMs`. */
  buffPlayerDamage?(mult: number, durationMs: number): void;
  /** dominion-2: instantly deal `amount` damage to all enemies on screen. */
  damageAllEnemies?(amount: number): void;
  /** dominion-3: make the player invincible for `durationMs`. */
  setPlayerInvincible?(durationMs: number): void;
  /** dominion-3: multiply player attack speed by `mult` for `durationMs`. */
  setPlayerAttackSpeedMult?(mult: number, durationMs: number): void;
}
